import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { SelectChangeEvent } from '@mui/material';
import { debounce, upperCase, capitalize } from 'lodash';

import { paths } from '@common/types';
import { MAX_PINNED_COLUMNS } from '@common/types/constants';
import {
  useOrganizationsShortQuery,
  usePartnerShortQuery,
  usePracticeShortQuery,
  useTeamsCsvDownloadQuery,
} from '@common/cache';
import { formatApiError } from '@common/errors/error-formatting.ts';
import {
  useTeamTableStore,
  PersistedTeamColumn,
  initialTeamColumns,
} from '../Filters/filters.store';
import {
  GridColDef,
  GridColumnOrderChangeParams,
  GridColumnResizeParams,
  GridPaginationModel,
  GridPinnedColumnFields,
  GridSortDirection,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import { Box, Tooltip, Typography } from '@mui/material';
import { TruncatedTextWithConditionalTooltip } from '@components';
import DiamondIcon from '@mui/icons-material/Diamond';
import { buildGridColumns, getReorderedColumns } from '@util/dataGrid';

export type ITeamTableEntry =
  paths['/record/teams']['get']['responses']['200']['content']['application/json']['teams'][number];

export type { ITeamTableFilters } from '../Filters/filters.store';

const buildTeamColumns = (isDense: boolean): GridColDef<ITeamTableEntry>[] => [
  {
    field: 'name',
    headerName: 'Name',
    minWidth: 200,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
        <DiamondIcon
          sx={{
            width: 24,
            height: 24,
            marginRight: 1,
            color: params.row.color,
          }}
        />
        <TruncatedTextWithConditionalTooltip
          text={params.row.name}
          variant='body2'
          width='100%'
        />
      </Box>
    ),
  },
  {
    field: 'description',
    headerName: 'Description',
    minWidth: 180,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        text={params.row.description || '-'}
        variant='body2'
        width='100%'
      />
    ),
  },
  {
    field: 'city',
    headerName: 'City / State',
    minWidth: 150,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <TruncatedTextWithConditionalTooltip
          variant='body2'
          text={params.row.city}
          width='100%'
        />
        {!isDense && (
          <TruncatedTextWithConditionalTooltip
            color='textSecondary'
            variant='caption'
            text={params.row.state}
            width='100%'
          />
        )}
      </Box>
    ),
  },
  {
    field: 'managing_entity',
    headerName: 'Managing Entity',
    minWidth: 180,
    flex: 1,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TruncatedTextWithConditionalTooltip
          text={params.row.managing_entity.name}
          variant='body2'
          width='100%'
        />
        {!isDense && (
          <TruncatedTextWithConditionalTooltip
            variant='caption'
            color='textSecondary'
            text={capitalize(params.row.managing_entity.type)}
            width='100%'
          />
        )}
      </Box>
    ),
  },
  {
    field: 'other_assignments',
    headerName: 'Other Assignments',
    width: 160,
    minWidth: 160,
    sortable: false,
    filterable: false,
    renderCell: params => (
      <Tooltip
        title={
          <>
            {params.row.additional_entities.map((o, idx) => (
              <div key={idx}>
                {capitalize(o.type)}: {o.name}
              </div>
            ))}
          </>
        }
        placement='bottom-start'
        slotProps={{
          popper: {
            modifiers: [{ name: 'offset', options: { offset: [0, 15] } }],
          },
        }}
      >
        <Typography variant='body1' sx={{ fontSize: '0.87rem' }}>
          {params.row.additional_entities?.length || '0'}
        </Typography>
      </Tooltip>
    ),
  },
  {
    field: 'user_count',
    headerName: 'Users',
    width: 100,
    minWidth: 100,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        variant='body2'
        text={params.row.user_count}
        width='100%'
      />
    ),
  },
  {
    field: 'lead_user',
    headerName: 'Lead User',
    minWidth: 160,
    flex: 1,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        variant='body2'
        text={params.row.lead_user}
        width='100%'
      />
    ),
  },
  {
    field: 'patient_count',
    headerName: 'Patients',
    width: 110,
    minWidth: 110,
    sortable: true,
    filterable: false,
    renderCell: params => (
      <TruncatedTextWithConditionalTooltip
        variant='body2'
        text={params.row.patient_count}
        width='100%'
      />
    ),
  },
];

export const useTeamTable = () => {
  const {
    filters,
    setFilters,
    clearStore,
    columns: persistedColumns,
    setColumns: setPersistedColumns,
    pinnedColumns,
    setPinnedColumns,
    hiddenColumns,
    setHiddenColumns,
    density,
    setDensity,
    resetView,
  } = useTeamTableStore();
  const { sort_by, sort_order } = filters;

  const apiRef = useGridApiRef();

  const { data, isPending, mutateAsync } = useTeamsCsvDownloadQuery();
  const { data: partnerShortOptions = [] } = usePartnerShortQuery();
  const { data: organizationShortOptions = [] } = useOrganizationsShortQuery();
  const { data: practiceShortOptions = [] } = usePracticeShortQuery({});

  const [searchInputValue, setSearchInputValue] = useState('');
  const [recordTypeField, setRecordTypeField] = useState<string>('');
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const recordTypes = [
    { value: '', label: 'All' },
    { value: 'organization', label: 'Organization' },
    { value: 'partner', label: 'Partner' },
    { value: 'practice', label: 'Practice' },
  ];

  const recordTypeOptions = useMemo(() => {
    if (recordTypeField === '') {
      return [
        ...organizationShortOptions,
        ...partnerShortOptions,
        ...practiceShortOptions,
      ];
    }
    return (
      (
        {
          partner: partnerShortOptions,
          organization: organizationShortOptions,
          practice: practiceShortOptions,
        } as const
      )[recordTypeField] ?? []
    );
  }, [
    recordTypeField,
    partnerShortOptions,
    organizationShortOptions,
    practiceShortOptions,
  ]);

  useEffect(() => {
    if (data) {
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `teams`;
      function handleOnDownload() {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', handleOnDownload);
        }, 150);
      }
      a.addEventListener('click', handleOnDownload, false);
      a.click();
    }
  }, [data]);

  useEffect(() => {
    return () => setFilters({ search: '', state: '', record_id: '' });
  }, []);

  const handleDownload = async () => {
    const loadingToast = toast.loading('Downloading Teams Table...', {
      isLoading: true,
      autoClose: 500,
    });
    try {
      await mutateAsync({
        sort_by: filters.sort_by as typeof sort_by & undefined,
        sort_order: upperCase(filters.sort_order) as 'ASC' | 'DESC',
        ...(filters.search !== '' && { search: filters.search }),
        ...(filters.state !== '' && { state: filters.state }),
        ...(filters.record_id !== '' && { record_id: filters.record_id }),
      });
      toast.success('Downloaded');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      toast.update(loadingToast, { isLoading: false, autoClose: 1 });
    }
  };

  const handleChangeOrder = (field: string, sort: GridSortDirection) => {
    if (!field || !sort) {
      setFilters({ sort_by: 'name', sort_order: 'asc' });
      return;
    }
    setFilters({ sort_by: field as typeof sort_by, sort_order: sort });
  };

  const handleChangeStateFilter = (e: SelectChangeEvent<string>) => {
    setFilters({ state: e.target.value });
  };

  const handleChangeRecordFilter = (e: SelectChangeEvent<string>) => {
    setFilters({ record_id: e.target.value });
  };

  const onPageModelChange = (model: GridPaginationModel) => {
    setFilters({ limit: model.pageSize, page: model.page + 1 });
  };

  const debouncedSetSearch = useMemo(
    () => debounce((value: string) => setFilters({ search: value }), 500),
    [filters, setFilters],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchInputValue(value);
    debouncedSetSearch(value);
  };

  const runtimeColumns = useMemo(
    () => buildTeamColumns(density === 'dense'),
    [density],
  );
  const columns = buildGridColumns<ITeamTableEntry, PersistedTeamColumn>(
    runtimeColumns,
    persistedColumns,
  );

  const toPersistedColumn = (
    col: GridColDef<ITeamTableEntry>,
  ): PersistedTeamColumn => ({
    field: col.field,
    headerName: col.headerName,
    width: col.width,
    minWidth: col.minWidth,
    flex: col.flex,
    sortable: col.sortable,
    filterable: col.filterable,
    disableColumnMenu: col.disableColumnMenu,
    align: col.align,
    headerAlign: col.headerAlign,
  });

  const handleColumnOrderChange = (_params: GridColumnOrderChangeParams) => {
    const next = getReorderedColumns(apiRef, columns);
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handleColumnWidthChange = (params: GridColumnResizeParams) => {
    const next = columns.map(col =>
      col.field === params.colDef.field
        ? { ...col, width: params.width, flex: undefined }
        : col,
    );
    setPersistedColumns(next.map(toPersistedColumn));
  };

  const handlePinnedColumnsChange = (model: GridPinnedColumnFields) => {
    setPinnedColumns({
      left: (model.left ?? []).slice(0, MAX_PINNED_COLUMNS),
      right: (model.right ?? []).slice(0, MAX_PINNED_COLUMNS),
    });
  };

  const columnVisibilityModel: Record<string, boolean> = Object.fromEntries(
    columns.map(col => [col.field, !hiddenColumns.includes(col.field)]),
  );

  const handleColumnVisibilityModelChange = (
    model: Record<string, boolean>,
  ) => {
    setHiddenColumns(
      columns.map(col => col.field).filter(field => model[field] === false),
    );
  };

  const MIN_VISIBLE_COLUMNS = 4;
  const visibleColumnsCount = Object.values(columnVisibilityModel).filter(
    Boolean,
  ).length;
  const canHideColumn = visibleColumnsCount > MIN_VISIBLE_COLUMNS;
  const columnsWithHideable = columns.map(col => ({
    ...col,
    hideable: canHideColumn,
  }));

  const handleRecordTypeChange = (value: string) => {
    setRecordTypeField(value);
    setFilters({ record_id: '' });
  };

  const handleResetAll = () => {
    clearStore();
    setSearchInputValue('');
    setRecordTypeField('');
    resetView();
  };

  return {
    apiRef,
    filters,
    columns: columnsWithHideable,
    onPageModelChange,
    handleSearchChange,
    recordTypes,
    handleChangeRecordFilter,
    handleChangeStateFilter,
    handleChangeOrder,
    handleDownload,
    isDownloading: isPending,
    searchInputValue,
    recordTypeField,
    setRecordTypeField: handleRecordTypeChange,
    recordTypeOptions: recordTypeOptions.map(o => ({
      value: o.id,
      label: o.name,
    })),
    sort_by,
    sort_order,
    handleResetAll,
    initialSortFilters: { sort_order: 'asc', sort_by: 'name' },
    isResetModalOpen,
    setIsResetModalOpen,
    pinnedColumns,
    handlePinnedColumnsChange,
    columnVisibilityModel,
    handleColumnVisibilityModelChange,
    handleColumnOrderChange,
    handleColumnWidthChange,
    density,
    handleDensityChange: setDensity,
    initialColumns: initialTeamColumns,
  };
};
